/* eslint-disable no-param-reassign */
import get from 'lodash.get';
import { fireUtagEvent } from '@garmin/support-analytics';
// eslint-disable-next-line import/no-cycle
import Utils from '../../../../lib/utils';
import PageTitle from '../../../../lib/pageTitle';
import { capi } from '../../client';
import {
  getProductTagNameFromId,
  productListByTerm,
  productMatchByProductId,
  isProductContactOptionsDisabled,
} from './util';

const getDefaultState = () => ({
  error: false,
  errorMessage: false,
  errorOverrideMessage: false,
  genericError: false,
  specificityErrorMessage: false,
  zeroResultsErrorMessage: false,
  errorSearchTerm: null,
  product: false,
  faqSideDoorSearchError: false,
  productDetailsFaqSearch: false,
  productFilteredList: [],
  productList: [],
  productListAutocomplete: [],
  productSearchOverrides: [],
  productSearchErrorOverrides: [],
  productSearchTerm: false,
});

export default {
  state: getDefaultState(),

  getters: {
    getProductTagNameFromId,
    isProductContactOptionsDisabled,
    productListByTerm,
    productMatchByProductId,
    faqSideDoorSearchError: (state) => state.faqSideDoorSearchError,
    error: (state) => state.error,
    errorMessage: (state) => state.errorMessage,
    errorOverrideMessage: (state) => state.errorOverrideMessage,
    genericError: (state) => state.genericError,
    specificityErrorMessage: (state) => state.specificityErrorMessage,
    zeroResultsErrorMessage: (state) => state.zeroResultsErrorMessage,
    errorSearchTerm: (state) => state.errorSearchTerm,
    productDetailsFaqSearch: (state) => state.productDetailsFaqSearch,
    productSearchTerm: (state) => state.productSearchTerm,
    productList: (state) => state.productList,
    productFilteredList: (state) => state.productFilteredList,
    productListAutocomplete: (state) => state.productListAutocomplete,
    product: (state) => state.product,
    productDisplayName: (state) => state.product.displayName || '',
    hasUserIdentifiedProduct: (state, getters) => getters.product,
    productMatchByTerm: (state) => (term) => state.productList.filter(
      (product) => (
        term
        && (
          (product?.displayName || '').toLowerCase() === term.toLowerCase()
          || product?.searchName.toLowerCase() === term.toLowerCase()
        )
      ),
    ),
    productSearchOverrideByTerm: (state) => (term) => state.productSearchOverrides.filter(
      (override) => override.productSearchText === term,
    ),
    productSearchErrorOverrideByTerm: (state) => (term) => state.productSearchErrorOverrides.filter(
      (override) => override.searchText.toLowerCase() === term.toLowerCase(),
    ),
    isProductFaqRelatedContent: (state, getters) => (productTags) => (
      (productTags || []).some(
        (t) => ((getters.faqDetails || {}).tags || []).includes(t),
      )
    ),
  },

  mutations: {
    resetProductModule(state) {
      Object.assign(state, getDefaultState());
    },

    productUpdate(state, product) {
      // handle gbase product
      if (product && (product.productId === null
        || typeof product.productId === 'undefined')
        && product.displayName) {
        // eslint-disable-next-line no-param-reassign
        product.productId = product.displayName;
      }
      state.product = product;
    },
    productFilteredListUpdate(state, list) {
      state.productFilteredList = list;
    },

    productListUpdate(state, list) {
      state.productList = list;
    },

    productListAutocompleteUpdate(state, list) {
      const formattedList = [];
      if (Array.isArray(list)) {
        list.forEach((item) => {
          let partNumber = false;

          if (item.partNumber) partNumber = item.partNumber;

          if (item.displayName && item.productId) {
            const product = {
              label: item.displayName,
              value: {
                productId: item.productId,
                partNumber,
              },
            };

            formattedList.push(product);
          }
        });
      }

      state.productListAutocomplete = formattedList;
    },

    productSearchOverridesUpdate(state) {
      const overrides = get(
        window?.AppData?.translations,
        'references.scProductSelectionOverrides.productOverrideList',
        null,
      );
      if (overrides) state.productSearchOverrides = Object.values(overrides);
    },

    productSearchErrorOverridesUpdate(state) {
      const overrides = get(
        window?.AppData?.translations,
        'references.scProductErrorOverrides.productErrorOverrideList',
        null,
      );

      if (overrides) state.productSearchErrorOverrides = Object.values(overrides);
    },

    productSearchTermUpdate(state, term) {
      state.productSearchTerm = term;
    },

    productDetailsFaqSearchUpdate(state, details) {
      state.productDetailsFaqSearch = details;
    },
    faqSideDoorSearchErrorUpdate(state, update) {
      state.faqSideDoorSearchError = update;
    },
    errorUpdate(state, error) {
      state.error = error;
    },
    errorMessageUpdate(state, errorMessage) {
      state.errorMessage = errorMessage;
    },
    errorOverrideMessageUpdate(state, errorOverrideMessage) {
      state.errorOverrideMessage = errorOverrideMessage;
    },
    genericErrorUpdate(state, genericError) {
      state.genericError = genericError;
    },
    specificityErrorMessageUpdate(state, specificityErrorMessage) {
      state.specificityErrorMessage = specificityErrorMessage;
    },
    zeroResultsErrorMessageUpdate(state, zeroResultsErrorMessage) {
      state.zeroResultsErrorMessage = zeroResultsErrorMessage;
    },
    errorSearchTermUpdate(state, errorSearchTerm) {
      state.errorSearchTerm = errorSearchTerm;
    },
  },

  actions: {
    resetProductModule({ commit }) {
      commit('resetProductModule');
    },
    resetSearchErrors({ commit }) {
      commit('errorUpdate', false);
      commit('errorMessageUpdate', false);
      commit('errorOverrideMessageUpdate', false);
      commit('genericErrorUpdate', false);
      commit('specificityErrorMessageUpdate', false);
      commit('zeroResultsErrorMessageUpdate', false);
    },
    updateProductSearchErrors({ commit }, { mutation, value }) {
      commit(`${mutation}Update`, value);
    },
    updateFaqSideDoorSearchError({ commit }, update) {
      commit('faqSideDoorSearchErrorUpdate', update);
    },

    async fetchProductDetails({
      dispatch, commit, rootGetters,
    }, { params }) {
      const apiId = 'fetchProductDetails';
      if (rootGetters[`isApiIdCallInProgress(${apiId})`]) return;
      commit('incrementApiCallsInProgress', apiId, { root: true });
      commit('productDetailsFaqSearchUpdate', null);

      const urlParams = { ...rootGetters.urlParams };
      let productId;
      let partNumber = false;

      if (params) {
        productId = params.productId;
        partNumber = params.partNumber;
      } else if (rootGetters.urlParams.partNumber) {
        partNumber = rootGetters.urlParams.partNumber;
      } else {
        // eslint-disable-next-line max-len
        productId = rootGetters.urlParams.identifier ? rootGetters.urlParams.identifier : rootGetters.urlParams.productID;
      }

      try {
        let override = '';
        if (window.AppData.overrideLocale) override = `?overrideLocale=${window.AppData.overrideLocale}`;
        const servicePath = `products/${rootGetters.locale}/${(partNumber || productId)}/${override}`;
        const { data } = await capi.get(servicePath);

        if (params.serialNumberSearchFlag) data.identifier = params.searchTerm;
        if (partNumber) data.partNumber = partNumber;

        if (params.faqSideDoorEnabled) {
          commit('productDetailsFaqSearchUpdate', data);
          return;
        }

        if (params?.serialNumberSearchFlag) urlParams.identifier = params.searchTerm;
        else if (partNumber) {
          urlParams.partNumber = partNumber;
          urlParams.productID = data.productId;
        } else urlParams.productID = data.productId || data.displayName;

        if (params.focus) {
          urlParams.tab = params.focus;
          urlParams.focus = false;
        }

        Utils.triggerWelcomeLoadAnalytics(urlParams);
        commit('urlParamsUpdate', urlParams);

        PageTitle.constructTitle([data.displayName, rootGetters.defaultPageTitle], '|', false);
        commit('topicsUpdate', (data?.topics || []));

        // eslint-disable-next-line max-len

        commit('productUpdate', data);

        await dispatch('repair/repairFaqIdUpdate', null);
        const hasRepairPID = window.AppData.translations.scTabs.scRepairTab.rightToRepairProducts.includes(data.productId);

        if (hasRepairPID) {
          await dispatch('repair/fetchRepairResults', data.tagName);
        }

        await dispatch('chat/fetchChatAvailability');

        if (rootGetters['chat/chatAvailability']) {
          fireUtagEvent({
            event_category: 'Product - Get Help',
            event_action: 'Contact Support - Chat - Status',
            event_label: 'Possible',
            product_name: data.displayName || '',
            ps_topic: rootGetters.getTopicTagFromId()?.englishTagName,
          });
        }
      } catch (e) {
        commit('productUpdate', getDefaultState().product);
      } finally {
        commit('decrementApiCallsInProgress', apiId, { root: true });
      }
    },
    async fetchProductListByLocale({ commit, getters, rootGetters }, version) {
      const apiId = 'fetchProductListByLocale';

      if ((getters.productList || []).length) return;
      if (rootGetters[`isApiIdCallInProgress(${apiId})`]) return;
      commit('incrementApiCallsInProgress', apiId, { root: true });

      try {
        let qs = '';
        if (version) qs = `?version=${version}`;
        const servicePath = `products/${getters.locale}/${qs}`;
        const { data } = await capi.get(servicePath);

        commit('productListUpdate', data);
        commit('productListAutocompleteUpdate', data);
      } catch (e) {
        commit('unavailableStateFlagUpdate', true);
      } finally {
        commit('decrementApiCallsInProgress', apiId, { root: true });
      }
    },
  },
};
